import React, { useEffect, useRef, useState } from 'react';
import { Button } from './ui/button';
import { Mail, Phone, MapPin, Download, ArrowRight } from 'lucide-react';
import { contactData } from '../data/mock';

const ContactSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const currentSection = sectionRef.current;
    
    if (currentSection) {
      observer.observe(currentSection);
    }

    return () => {
      const currentSection = sectionRef.current;
    
    if (currentSection) {
        observer.unobserve(currentSection);
      }
    };
  }, []);

  const handleDownloadPortfolio = () => {
    const link = document.createElement('a');
    link.href = '/A-To-Z-Infraspares-Brochure.pdf';
    link.download = 'A-To-Z-Infraspares-Brochure.pdf';
    link.click();
  };

  const scrollToQuoteForm = () => {
    const element = document.getElementById('quote-form');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section ref={sectionRef} id="contact" className="py-24 bg-gradient-to-b from-gray-900 to-gray-800 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }} />
      </div>

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className={`text-center mb-16 transform transition-all duration-700 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Eliminate Downtime?
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Get in touch with our team of experts. We're here to help you find the right parts for your machinery.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div className={`space-y-8 transform transition-all duration-700 delay-200 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}>
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-white mb-6">Contact Information</h3>
                
                <div className="space-y-6">
                  {/* Company Name */}
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-400 mb-1">Company</p>
                      <p className="text-white font-semibold text-lg">
                        {contactData.companyName}
                      </p>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-400 mb-1">Email</p>
                      <a 
                        href={`mailto:${contactData.email}`}
                        className="text-white font-semibold text-lg hover:text-green-400 transition-colors"
                      >
                        {contactData.email}
                      </a>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-400 mb-1">Phone</p>
                      <a 
                        href={`tel:${contactData.phone}`}
                        className="text-white font-semibold text-lg hover:text-green-400 transition-colors"
                      >
                        {contactData.phone}
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Download Portfolio Card */}
              <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-8 shadow-2xl">
                <h3 className="text-2xl font-bold text-white mb-3">
                  Download Our Full Portfolio
                </h3>
                <p className="text-green-100 mb-6">
                  Get detailed information about all our products, brands, and services.
                </p>
                <Button
                  onClick={handleDownloadPortfolio}
                  className="w-full bg-white text-green-500 hover:bg-gray-100 font-semibold"
                >
                  <Download className="mr-2 w-5 h-5" />
                  Download Portfolio
                </Button>
              </div>
            </div>

            {/* CTA Section */}
            <div className={`flex flex-col justify-center transform transition-all duration-700 delay-400 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}>
              <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-10">
                <h3 className="text-3xl font-bold text-white mb-4">
                  Need a Quote Right Away?
                </h3>
                <p className="text-gray-300 text-lg mb-8 leading-relaxed">
                  Fill out our quick quote request form and our team will get back to you with a competitive quote within 24 hours. We guarantee the best prices and fastest delivery times.
                </p>
                <Button
                  onClick={scrollToQuoteForm}
                  size="lg"
                  className="w-full bg-green-500 hover:bg-green-700 text-white text-lg font-semibold py-6 shadow-2xl group"
                >
                  Request a Quote Now
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>

                <div className="mt-8 pt-8 border-t border-white/10">
                  <p className="text-gray-400 text-sm text-center">
                    Trusted by hundreds of businesses across India
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
